import { useState } from 'react'
import type { ReactNode } from 'react'
import { Check, Copy, Loader2 } from 'lucide-react'
import { Button } from '~/components/ui/button'
import { cn } from '~/lib/utils'
import { env } from '~/env'

export type Records = Array<Record<string, string>>

/** 백엔드가 준 상대 ingestUrl을 절대 URL로 — API_BASE_URL이 상대('/api')면 현재 출처를 붙인다. */
export function toAbsoluteUrl(path: string): string {
    if (/^https?:\/\//.test(path)) return path
    const suffix = path.startsWith('/') ? path : `/${path}`
    const base = env.API_BASE_URL.replace(/\/$/, '')
    if (/^https?:\/\//.test(base)) return `${base}${suffix}`
    const origin = typeof window !== 'undefined' ? window.location.origin : ''
    return `${origin}${suffix}`
}

/** 이미지 파일을 최대 변(maxDim)으로 축소한 JPEG base64로 변환(페이로드·VLM 비용 절감). */
export async function fileToScaledBase64(
    file: File,
    maxDim = 1600
): Promise<{ base64: string; mime: string }> {
    const dataUrl = await new Promise<string>((resolve, reject) => {
        const r = new FileReader()
        r.onload = () => resolve(r.result as string)
        r.onerror = reject
        r.readAsDataURL(file)
    })
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const im = new Image()
        im.onload = () => resolve(im)
        im.onerror = reject
        im.src = dataUrl
    })
    const scale = Math.min(1, maxDim / Math.max(img.width, img.height))
    const w = Math.round(img.width * scale)
    const h = Math.round(img.height * scale)
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    canvas.getContext('2d')?.drawImage(img, 0, 0, w, h)
    const out = canvas.toDataURL('image/jpeg', 0.85)
    return { base64: out.split(',')[1] ?? '', mime: 'image/jpeg' }
}

/** 셀 값을 문자열로. 날짜(엑셀)는 'YYYY-MM-DD HH:mm', 빈칸은 ''. */
function cellToString(cell: unknown): string {
    if (cell == null) return ''
    if (cell instanceof Date)
        return cell.toISOString().slice(0, 16).replace('T', ' ')
    return String(cell).trim()
}

/**
 * CSV·XLSX 파일 → {컬럼, 레코드}. 확장자/타입이 xlsx면 첫 시트를 읽고, 아니면 CSV로 파싱한다.
 * (양식은 CSV지만 사용자가 Excel에서 열어 .xlsx로 저장해 올리는 경우가 많아 둘 다 지원한다.)
 */
export async function parseSpreadsheet(
    file: File
): Promise<{ columns: string[]; records: Records }> {
    const name = file.name.toLowerCase()
    const isXlsx = name.endsWith('.xlsx') || file.type.includes('spreadsheetml')
    if (!isXlsx) {
        return parseCsv(await file.text())
    }
    // read-excel-file: 읽기 전용·경량. 브라우저 엔트리를 필요할 때만 동적 로드(메인 번들 부담↓).
    const { default: readXlsxFile } = await import('read-excel-file/browser')
    // 스키마 없이 호출하면 런타임상 행(셀 배열)의 배열을 돌려준다.
    const rows = (await readXlsxFile(file)) as unknown as unknown[][]
    const nonEmpty = rows.filter((r) =>
        r.some((c) => c != null && String(c).trim() !== '')
    )
    if (nonEmpty.length === 0) return { columns: [], records: [] }
    const columns = nonEmpty[0].map(cellToString)
    const records = nonEmpty.slice(1).map((row) => {
        const rec: Record<string, string> = {}
        columns.forEach((col, i) => {
            rec[col] = cellToString(row[i])
        })
        return rec
    })
    return { columns, records }
}

/** CSV 텍스트 → {컬럼, 레코드}. Phase 1용 단순 파서(따옴표 미지원). */
export function parseCsv(text: string): {
    columns: string[]
    records: Records
} {
    const lines = text.trim().split(/\r?\n/).filter(Boolean)
    if (lines.length === 0) return { columns: [], records: [] }
    const columns = lines[0].split(',').map((s) => s.trim())
    const records = lines.slice(1).map((line) => {
        const cells = line.split(',')
        const rec: Record<string, string> = {}
        columns.forEach((c, i) => {
            rec[c] = (cells[i] ?? '').trim()
        })
        return rec
    })
    return { columns, records }
}

/** 클립보드 복사 버튼(복사 후 잠시 체크 아이콘). */
export function CopyButton({ text, label }: { text: string; label?: string }) {
    const [done, setDone] = useState(false)
    return (
        <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={() => {
                void navigator.clipboard?.writeText(text).then(() => {
                    setDone(true)
                    setTimeout(() => setDone(false), 1500)
                })
            }}>
            {done ? <Check /> : <Copy />}
            {label ?? (done ? '복사됨' : '복사')}
        </Button>
    )
}

/** 드래그앤드롭 + 클릭 파일 드롭존. multiple+onFiles면 여러 파일을 한 번에 처리한다. */
export function Dropzone({
    accept,
    onFile,
    onFiles,
    multiple = false,
    busy,
    icon,
    title,
    hint,
}: {
    accept: string
    onFile: (file: File) => void
    onFiles?: (files: File[]) => void
    multiple?: boolean
    busy: boolean
    icon: ReactNode
    title: string
    hint: string
}) {
    const [over, setOver] = useState(false)
    const handle = (list: FileList | null) => {
        if (busy || !list || list.length === 0) return
        const files = Array.from(list)
        if (multiple && onFiles) onFiles(files)
        else onFile(files[0])
    }
    return (
        <label
            onDragOver={(e) => {
                e.preventDefault()
                setOver(true)
            }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => {
                e.preventDefault()
                setOver(false)
                handle(e.dataTransfer.files)
            }}
            className={cn(
                'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed px-4 py-7 text-center transition-colors',
                over
                    ? 'border-primary bg-accent'
                    : 'border-border hover:border-primary hover:bg-muted/40',
                busy && 'pointer-events-none opacity-60'
            )}>
            <div className='bg-accent text-primary grid size-10 place-items-center rounded-full'>
                {busy ? <Loader2 className='size-5 animate-spin' /> : icon}
            </div>
            <span className='text-foreground text-sm font-medium'>{title}</span>
            <span className='text-muted-foreground text-xs'>{hint}</span>
            <input
                type='file'
                accept={accept}
                multiple={multiple}
                className='hidden'
                disabled={busy}
                onChange={(e) => {
                    handle(e.target.files)
                    e.target.value = ''
                }}
            />
        </label>
    )
}
